// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
/*
 * This kind of conversion could be done in the API. Putting it here for the time being.
 */
import { hgPrice } from '@smg-real-estate/domus';
import {
  Address,
  Attachment,
  AttachmentType,
  Buy,
  L10N,
  Listing,
  OfferType,
  Prices,
  Rent,
  Url,
  UrlType,
} from '@smg-real-estate/hg-rets-schema';

// import Vue from 'vue';
import {
  getCloudinaryUrlForTransformation,
  HgCloudinaryNamedTransformation,
} from '@/helpers/Cloudinary/CloudinaryHelper';
import {
  type HgListingAttachedDocument,
  type HgListingVideoMovie,
  HgListingVideoMovieTypes,
  type HgListingVirtualTour,
  HgListingVirtualTourTypes,
} from '@/models';
import { getEmbeddedYouTubeUrl, isYouTubeVideoUrl } from '@/models/HgListingVideoMovie';
import { getVirtualTourUrl, isVirtualTourUrl } from '@/models/HgListingVirtualTour';

export function getListingDefaultLocale(listing: Listing): L10N | undefined {
  switch (listing.localization?.primary) {
    case 'en':
      return listing.localization?.en;
    case 'it':
      return listing.localization?.it;
    case 'fr':
      return listing.localization?.fr;
    case 'de':
    default:
      return listing.localization?.de;
  }
}

export function getListingImages(listing: Listing): Attachment[] | undefined {
  return getListingDefaultLocale(listing)?.attachments?.filter(
    (attachment) => attachment.type === AttachmentType.Image,
  );
}

export function getListingImage(listing: Listing, index: number): Attachment | undefined {
  return getListingImages(listing)?.[index];
}

export function getNumberOfImagesFromListing(listing: Listing): number {
  return getListingImages(listing)?.length ?? 0;
}

export function getListingPriceData(listing: Listing): Buy | Rent | undefined {
  return listing.offerType === OfferType.Buy ? listing.prices?.buy : listing.prices?.rent;
}

export function getListingCurrency(listing: Listing): string | undefined {
  return listing.prices?.currency;
}

export function getListingPriceArea(listing: Listing): string | undefined {
  return getListingPriceData(listing)?.area;
}

export function getListingPriceInterval(listing: Listing): string | undefined {
  return listing.offerType === OfferType.Rent ? listing.prices?.rent?.interval : undefined;
}

export function getListingDisplayPrice(listing: Listing): number | undefined {
  const priceData = getListingPriceData(listing);

  if (!priceData) {
    return undefined;
  }

  return (priceData as Rent).gross ?? (priceData as Rent).net ?? (priceData as Buy).price;
}

export function getListingRooms(listing: Listing): number | undefined {
  return listing.characteristics?.numberOfRooms;
}

export function getListingLivingSpace(listing: Listing): number | undefined {
  return listing.characteristics?.livingSpace;
}

export function getListingLink(listingDefaultLocale: L10N): Url | null {
  return (
    listingDefaultLocale.urls?.find((url: Url) => {
      const isLink = url.type && url.type === UrlType.Link;
      return isLink && url.value && !isVirtualTourUrl(url.value);
    }) ?? null
  );
}

const getListingUrls = (listingDefaultLocale: L10N | undefined): Url[] =>
  listingDefaultLocale?.urls ?? [];

const getListingAttachments = (listingDefaultLocale: L10N | undefined): Attachment[] =>
  listingDefaultLocale?.attachments ?? [];

export function getListingVirtualTour(
  listingDefaultLocale: L10N | undefined,
): HgListingVirtualTour | undefined {
  const listingVirtualTours: HgListingVirtualTour[] = getListingUrls(listingDefaultLocale)
    .filter((url) => url.type && url.type in HgListingVirtualTourTypes)
    .map((url) => ({
      src: url.value,
    }));

  if (listingVirtualTours.length > 0) {
    return listingVirtualTours[0];
  }

  // Additionally search in the urls for type LINK (legacy).
  const listingLinks: HgListingVirtualTour[] = getListingUrls(listingDefaultLocale)
    .filter((url) => {
      const isLink = url.type && url.type === UrlType.Link;
      return isLink && url.value && isVirtualTourUrl(url.value);
    })
    .map((url) => ({
      src: url.value && getVirtualTourUrl(url.value),
    }));

  return listingLinks.length > 0 ? listingLinks[0] : undefined;
}

export function getListingVideoMovie(
  listingDefaultLocale: L10N,
  locale = 'de',
): HgListingVideoMovie | undefined {
  const listingVideos: HgListingVideoMovie[] = getListingUrls(listingDefaultLocale)
    .filter((url) => {
      const isMovie = url.type && url.type in HgListingVideoMovieTypes;
      return isMovie && url.value && isYouTubeVideoUrl(url.value);
    })
    .map((url) => ({
      src: url.value && getEmbeddedYouTubeUrl(url.value, locale),
      title: url.title,
    }));

  if (listingVideos.length > 0) {
    return listingVideos[0];
  }

  // Additionally search in the attachment for videos that are a YouTube video.
  const listingMovies: HgListingVideoMovie[] = getListingAttachments(listingDefaultLocale)
    .filter((attachment) => {
      const isMovie = attachment.type && attachment.type === AttachmentType.Movie;
      return isMovie && attachment.url && isYouTubeVideoUrl(attachment.url);
    })
    .map((attachment) => ({
      src: attachment.url && getEmbeddedYouTubeUrl(attachment.url, locale),
      title: attachment.title,
    }));

  return listingMovies.length > 0 ? listingMovies[0] : undefined;
}

export function getListingAttachedDocument(
  listingDefaultLocale: L10N,
): HgListingAttachedDocument | undefined {
  const listingDocuments: HgListingAttachedDocument[] = getListingAttachments(listingDefaultLocale)
    .filter((attachment: Attachment) => {
      return (
        attachment.type &&
        [AttachmentType.Document, AttachmentType.Plan, AttachmentType.SalesBrochure].includes(
          attachment.type,
        )
      );
    })
    .map((attachment: Attachment) => ({
      href: attachment.url,
      label: attachment.title,
    }));
  return listingDocuments.length > 0 ? listingDocuments[0] : undefined;
}

export function isRemoteViewingAvailable(listing: Listing): boolean {
  const magicStrings = [
    'remote viewing',
    'virtuelle besichtigung',
    'visite à distance',
    'visite a distanza',
  ];

  const viewingNote = (listing.lister?.contacts?.viewing?.note ?? '').toLowerCase();

  return magicStrings.some((magicString) => viewingNote.includes(magicString));
}

export function withCloudinaryTransformation(attachment: Attachment): {
  alt: string;
  versions: {
    small: { width: number; url: string };
    large: { width: number; url: string };
    fullscreen: { width: number; url: string };
  };
} {
  return {
    alt: attachment.title ?? '',
    versions: {
      small: {
        url: getCloudinaryUrlForTransformation(
          attachment.url,
          HgCloudinaryNamedTransformation.SMALL,
        ),
        width: 800,
      },
      large: {
        url: getCloudinaryUrlForTransformation(
          attachment.url,
          HgCloudinaryNamedTransformation.LARGE,
        ),
        width: 1500,
      },
      fullscreen: {
        url: getCloudinaryUrlForTransformation(
          attachment.url,
          HgCloudinaryNamedTransformation.FULLSCREEN,
        ),
        width: 2000,
      },
    },
  };
}

export const listingIdsFrom = (listings: Listing[]): string[] =>
  listings.map((listing) => listing.id || '');

export const getRentGross = (prices: Prices, locale: string): string | undefined =>
  prices?.rent?.gross ? hgPrice(prices?.rent.gross, locale).toString() : undefined;

export const getBuyPrice = (prices: Prices, locale: string): string | undefined =>
  prices?.buy?.price ? hgPrice(prices?.buy.price, locale).toString() : undefined;

export function getStreetWithAddition(address: Address): string | undefined {
  return address.streetAddition ? address.street + address.streetAddition : address.street;
}

export function getListingAddress(listing: Listing): string | undefined {
  const address = listing?.address;

  if (!address) {
    return undefined;
  }

  const streetAndZip = [getStreetWithAddition(address), address.postalCode]
    .filter((item) => !!item)
    .join(', ');

  return [streetAndZip, address.locality].filter((item) => !!item).join(' ');
}
